import { useEffect, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Theme } from '@astryxdesign/core/theme';
import { InternationalizationProvider } from '@astryxdesign/core/i18n';
import { LinkProvider } from '@astryxdesign/core/Link';
import ruRU from '@astryxdesign/core/locales/ru-RU.generated.js';
import { qronstatTheme } from '@qronstat/ui/theme';
import { ChartSchemeContext } from '@qronstat/ui/charts';
import { DataTableLabelsProvider } from '@qronstat/ui';
import type { DataTableLabels } from '@qronstat/ui';
import { catalogs, intlLocale } from '@qronstat/i18n';
import { ApiError } from '../api/client';
import { PreferencesProvider, usePreferences } from './preferences';
import { RouterLink } from './RouterLink';
import { router } from './router';
import { DeviceCache } from './cache';
import { DeviceCacheContext } from './deviceCache';
import { BootScreen } from './BootScreen';
import { UpdatePrompt } from './UpdatePrompt';

const astryxMessages = { 'ru-RU': ruRU };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) =>
        !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 3,
      retryDelay: (attempt) => Math.min(30_000, 1500 * 2 ** attempt),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: false,
      networkMode: 'offlineFirst',
      gcTime: 7 * 24 * 60 * 60 * 1000,
    },
    mutations: { networkMode: 'online', retry: false },
  },
});

const deviceCache = new DeviceCache(queryClient);

function useRestored(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    const timeout = setTimeout(() => alive && setReady(true), 1500);
    void deviceCache.restored.finally(() => {
      clearTimeout(timeout);
      if (alive) setReady(true);
    });
    return () => {
      alive = false;
      clearTimeout(timeout);
    };
  }, []);
  return ready;
}

function ThemedApp() {
  const { locale, themeMode, resolvedScheme } = usePreferences();
  const messages = useMemo(() => catalogs[locale], [locale]);
  const tableLabels = useMemo<DataTableLabels>(
    () => ({
      pagination: messages['common.pagination'] ?? '',
      sort: messages['table.sort'] ?? '',
      ascending: messages['table.ascending'] ?? '',
      descending: messages['table.descending'] ?? '',
    }),
    [messages],
  );
  const ready = useRestored();
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const color = getComputedStyle(document.body).backgroundColor;
      if (color)
        document
          .querySelectorAll('meta[name="theme-color"]')
          .forEach((m) => m.setAttribute('content', color));
    });
    return () => cancelAnimationFrame(frame);
  }, [resolvedScheme]);
  return (
    <Theme theme={qronstatTheme} mode={themeMode}>
      <InternationalizationProvider locale={intlLocale[locale]} messages={astryxMessages}>
        <IntlProvider locale={intlLocale[locale]} messages={messages} defaultLocale="ru-RU">
          <ChartSchemeContext.Provider value={resolvedScheme}>
            <DataTableLabelsProvider value={tableLabels}>
              <LinkProvider component={RouterLink}>
                {ready ? <RouterProvider router={router} /> : <BootScreen />}
                <UpdatePrompt />
              </LinkProvider>
            </DataTableLabelsProvider>
          </ChartSchemeContext.Provider>
        </IntlProvider>
      </InternationalizationProvider>
    </Theme>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <DeviceCacheContext.Provider value={deviceCache}>
        <PreferencesProvider>
          <ThemedApp />
        </PreferencesProvider>
      </DeviceCacheContext.Provider>
    </QueryClientProvider>
  );
}
